import { createContext, useEffect, useState } from "react";
import { useErrorContext } from "../../global/hooks/useErrorContext";
import { useAuthenticationContext } from "../../global/hooks/useAuthenticationContext";
import { IReportsContextProps } from "../types/interfaces/IReportsContextProps";
import { IReportsProviderProps } from "../types/interfaces/IReportsProviderProps";
import { IReportsList } from "../types/interfaces/IReportsList";
import { IReport } from "../types/interfaces/IReport";
import { IReportGenerationData } from "../types/interfaces/IReportGenerationData";

//The reports context object holds the latest value of the state.
export const ReportsContext = createContext<IReportsContextProps | undefined>(undefined);

//This component is responsible to share the reports data between components.
export const ReportsProvider: React.FC<IReportsProviderProps> = ({ children }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [reportsList, setReportsList] = useState<IReportsList | null>({ List: [] });
    const [selectedReport, setSelectedReport] = useState<IReport | null>(null);
    const [reportGenerationData, setReportGenerationData] = useState<IReportGenerationData | null>(null);

    const { cstKey } = useAuthenticationContext();
    const { setError } = useErrorContext();

    const API_GET_REPORTS = process.env.REACT_APP_API_GET_REPORTS;
    const API_GET_REPORT = process.env.REACT_APP_API_GET_REPORT;
    const INELIGIBLE_ERROR_TEXT: string | null = process.env.REACT_APP_REPORTS_INELIGIBLE_ERROR_TEXT ?? null;

    const handleError = (message: string | null, statusCode: number) => {  
        setError({ message, statusCode });
        setIsLoading(false);
    };

    //Function to fetch data using the get aap reports api endpoint.
    const fetchReports = async () => {
        try {
            const apiGetReports = `${API_GET_REPORTS}${cstKey}`;
            const response = await fetch(apiGetReports);
            if (!response.ok) {
                console.log('get', response.statusText);
                if (response.status === 401 || response.status === 403) {
                    return handleError(INELIGIBLE_ERROR_TEXT, response.status);
                } else {
                    return handleError(null, 1);
                }
            }
            const result = await response.json();
            if (!result) {
                return handleError(null, 2);
            } else {
                if (result.CustomerKey === null || (!result.Succeeded && result.Errors !== null)) {
                    return handleError(null, 3);
                }
                setReportsList({ List: result.Data });
                setIsLoading(false);
            }
        } catch (error: any) {
            console.log('get error', error);
            handleError(null, error.status);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to fetch a single report's generation data.
    const fetchReportGenerationData = async (reportKey: string, report: IReport) => {
        setIsLoading(true);
        setSelectedReport(report);
        setReportGenerationData(null);
        try {
            const response = await fetch(`${API_GET_REPORT}${reportKey}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(report)
            });
            if (!response.ok) {
                return handleError("Failed to fetch report data", response.status);
            }
            const result = await response.json();
            if (!result || !result.Data) {
                return handleError("No report data returned", 2);
            }
            setReportGenerationData(result.Data);
        } catch (error: any) {
            handleError("Error fetching report data", error.status);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (cstKey !== null && cstKey !== undefined) {
            fetchReports();
        }
    }, [cstKey]);
    
    const value = {
        reportsList,
        isLoading,
        selectedReport,
        reportGenerationData,
        fetchReportGenerationData,
        setSelectedReport
    };

    return (
        <ReportsContext.Provider value={value}>
            {children}
        </ReportsContext.Provider>
    );
};